import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { motion, useTransform, type MotionValue } from 'motion/react';
import { customRichTextTags } from '@/utils/i18n';
import { SCENE_START } from './reanalysis-timeline';

// 1920×1080 고정 캔버스를 축소해 그리므로 뷰포트별 줄바꿈 분기가 없다 — 항상 줄바꿈하는 <br>
const richTags = { br: customRichTextTags.solidBr };

const PATIENTS = [
  { key: 'development', x: 311, y: 126, days: 128 },
  { key: 'muscle', x: 1304, y: 795, days: 389 },
  { key: 'hypotonia', x: 1336, y: 48, days: 46 },
  { key: 'inflammation', x: 213, y: 617, days: 12 },
] as const;

export function PatientCard({
  patient,
  days,
  featured = false,
}: {
  patient: (typeof PATIENTS)[number]['key'] | 'cafe-au-lait';
  days: number;
  featured?: boolean;
}) {
  const t = useTranslations('Main.reanalysis');
  return (
    <div
      data-featured={featured}
      className="group/patient flex h-full w-full flex-col justify-between rounded-[20px] bg-white/50 p-8 text-3b-text-primary data-[featured=true]:rounded-[32px] data-[featured=true]:p-[51.2px]"
    >
      <div className="flex flex-col gap-4 group-data-[featured=true]/patient:gap-[25.6px]">
        <div className="relative h-[160px] w-[236px] overflow-hidden rounded-2xl group-data-[featured=true]/patient:h-[256px] group-data-[featured=true]/patient:w-[377.6px] group-data-[featured=true]/patient:rounded-[25.6px]">
          <Image
            src={`/images/main/reanalysis/${patient}.webp`}
            alt=""
            fill
            sizes={featured ? '480px' : '300px'}
            className="object-cover"
          />
        </div>
        <p className="text-[24px] font-semibold leading-8 tracking-[-0.464px] group-data-[featured=true]/patient:text-[38.4px] group-data-[featured=true]/patient:leading-[51.2px] group-data-[featured=true]/patient:tracking-[-0.7424px]">
          {t.rich(`patients.${patient}`, richTags)}
        </p>
      </div>
      <p className="bg-[linear-gradient(115deg,#090f19_33.544%,rgba(12,32,66,0.31)_101.14%)] bg-clip-text text-right text-[16px] font-medium leading-6 text-transparent group-data-[featured=true]/patient:text-[25.6px] group-data-[featured=true]/patient:leading-[38.4px]">
        {t('pending', { days })}
      </p>
    </div>
  );
}

export function PatientCards({ progress }: { progress: MotionValue<number> }) {
  const y = useTransform(progress, [SCENE_START.patients, 0.25], [1080, 0]);
  const opacity = useTransform(
    progress,
    [SCENE_START.patients, 0.2, SCENE_START.focus, 0.4, 0.46],
    [0, 1, 1, 0.2, 0],
  );
  return (
    <motion.div aria-hidden="true" style={{ y, opacity }}>
      {PATIENTS.map(patient => (
        <div
          key={patient.key}
          className="absolute h-[420px] w-[300px]"
          style={{ left: patient.x, top: patient.y }}
        >
          <PatientCard patient={patient.key} days={patient.days} />
        </div>
      ))}
    </motion.div>
  );
}
